import React, { useMemo } from "react";
import ReactEcharts from "echarts-for-react";
import { Col } from "react-bootstrap";
import { useSelector } from "react-redux";

const RejectionTrendChart = () => {
  const { claimDashboardData } = useSelector((state) => state.claimDashboard);

  // ✅ Collect last 6 months data objects (if exist)
  const monthsData = useMemo(
    () =>
      [
        claimDashboardData?.claim_previous_month5,
        claimDashboardData?.claim_previous_month4,
        claimDashboardData?.claim_previous_month3,
        claimDashboardData?.claim_previous_month2,
        claimDashboardData?.claim_previous_month,
        claimDashboardData?.claim_current_month,
      ].filter(Boolean),
    [claimDashboardData]
  );

  // ✅ Extract month labels (May 2025, Jun 2025, etc.)
  const monthLabels = useMemo(() => {
    return monthsData.map((month) => {
      const [year, monthNum] = month.month_year.split("-");
      const date = new Date(year, monthNum - 1);
      return date.toLocaleString("en-US", { month: "short", year: "numeric" });
    });
  }, [monthsData]);

  // ✅ Find all payors that appear in any month
  const payorNames = useMemo(() => {
    const names = new Set();
    monthsData.forEach((m) => {
      m.payer?.forEach((p) => {
        if (p.payor_name) names.add(p.payor_name);
      });
    });
    return Array.from(names);
  }, [monthsData]);

  // ✅ Compute rejection rates per payor per month
  const series = useMemo(() => {
    return payorNames.map((payorName) => {
      const data = monthsData.map((month) => {
        const payor = month.payer?.find((p) => p.payor_name === payorName);
        if (!payor) return 0;

        const { tot_status } = payor;
        const total = tot_status?.total || 0;
        const rejected = tot_status?.rejected || 0;
        return total ? ((rejected / total) * 100).toFixed(1) : 0;
      });
      return {
        name: payorName,
        type: "line",
        smooth: true,
        symbol: "circle",
        symbolSize: 7,
        data,
      };
    });
  }, [payorNames, monthsData]);

  // ✅ Define color palette
  const colors = ["#198754", "#dc3545", "#0dcaf0", "#6f42c1", "#d63384", "#fd7e14"];

  // ✅ ECharts options
  const option = useMemo(
    () => ({
      color: colors,
      title: {
        text: "Rejection Trend (Last 6 Months)",
        left: "left",
        textStyle: { fontSize: "1rem", color: "#2C7BEE", fontWeight: "normal" },
      },
      tooltip: {
        trigger: "axis",
        formatter: (params) => {
          let tooltip = `<strong>${params[0].axisValue}</strong><br/>`;
          params.forEach((item) => {
            tooltip += `
              <span style="display:inline-block;margin-right:5px;
                border-radius:50%;width:9px;height:9px;
                background-color:${item.color};"></span>
              ${item.seriesName}: ${item.data}%<br/>
            `;
          });
          return tooltip;
        },
      },
      legend: {
        bottom: 0,
        left: "center",
        data: payorNames,
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "15%",
        containLabel: true,
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: monthLabels,
        axisLine: { lineStyle: { color: "#999" } },
        axisLabel: { color: "#555" },
      },
      yAxis: {
        type: "value",
        name: "Rejection Rate (%)",
        nameLocation: "middle",
        nameGap: 60,
        nameTextStyle: { color: "#666", fontSize: 12 },
        min: 0,
        max: 100,
        axisLabel: { formatter: "{value}%" },
        splitLine: { lineStyle: { color: "#eee" } },
      },
      series,
    }),
    [series, monthLabels, payorNames, colors]
  );

  // ✅ Render
  return (
    <Col xs={12} className="p-0">
      <div className="card w-100" style={{ margin: 0 }}>
        <div className="card-body p-3">
          <ReactEcharts
            option={option}
            style={{ height: "400px", width: "100%" }}
            notMerge={true}
            lazyUpdate={true}
          />
        </div>
      </div>
    </Col>
  );
};

export default RejectionTrendChart;
